const COOKIE_CONSENT_KEY = "valoo_cookie_consent";
const CONSENT_VERSION = 2;
const OPEN_PREFERENCES_EVENT = "valoo:open-cookie-preferences";

// Optional categories declared in the Cookie Policy. Strictly necessary cookies
// are not listed because they cannot be declined. Adding a new tracker means
// adding its category here and gating it on hasConsent().
export const CONSENT_CATEGORIES = [
  {
    id: "analytics",
    label: "Analytics cookies",
    description:
      "Let us see how visitors use the dashboard so we can measure and improve performance. Sets Google Analytics cookies (_ga, _ga_*).",
  },
];

const buildCategories = (granted) =>
  CONSENT_CATEGORIES.reduce(
    (acc, category) => ({ ...acc, [category.id]: granted }),
    {}
  );

const readRaw = () => {
  try {
    return localStorage.getItem(COOKIE_CONSENT_KEY);
  } catch {
    return null;
  }
};

export const getConsent = () => {
  const raw = readRaw();
  if (!raw) return null;

  // Tolerate the bare 'all' | 'necessary' strings used by earlier builds rather
  // than re-prompting users who already answered.
  if (raw === "all") return { version: 1, categories: buildCategories(true) };
  if (raw === "necessary") {
    return { version: 1, categories: buildCategories(false) };
  }

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.categories) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const hasConsent = (categoryId) => {
  const consent = getConsent();
  return Boolean(consent && consent.categories[categoryId]);
};

export const hasAnalyticsConsent = () => hasConsent("analytics");

const listeners = new Set();

export const subscribeToConsent = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const ANALYTICS_COOKIE_PREFIXES = ["_ga", "_gid", "_gat"];

// Withdrawing consent has to remove cookies already on the device, not just
// stop setting new ones.
export const clearAnalyticsCookies = () => {
  const gaId = process.env.REACT_APP_GA4_MEASUREMENT_ID;
  // GA checks this global before every hit, so withdrawal applies immediately.
  if (gaId) window[`ga-disable-${gaId}`] = true;

  const { hostname } = window.location;
  const domains = [
    null,
    hostname,
    `.${hostname}`,
    `.${hostname.split(".").slice(-2).join(".")}`,
  ];

  document.cookie.split(";").forEach((entry) => {
    const name = entry.split("=")[0].trim();
    if (!ANALYTICS_COOKIE_PREFIXES.some((prefix) => name.startsWith(prefix))) {
      return;
    }
    domains.forEach((domain) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${
        domain ? `; domain=${domain}` : ""
      }`;
    });
  });
};

export const saveConsent = (categories) => {
  const next = {
    version: CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
    categories: { ...buildCategories(false), ...categories },
  };

  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(next));
  } catch {
    // Consent not persisted — banner will reappear on next visit
  }

  if (!next.categories.analytics) {
    clearAnalyticsCookies();
  }

  listeners.forEach((listener) => listener(next));
  return next;
};

export const acceptAllConsent = () => saveConsent(buildCategories(true));

export const rejectAllConsent = () => saveConsent(buildCategories(false));

// Lets the footer link reopen the preferences dialog so consent can be changed
// or withdrawn at any time.
export const openCookiePreferences = () =>
  window.dispatchEvent(new Event(OPEN_PREFERENCES_EVENT));

export const subscribeToPreferencesRequest = (listener) => {
  window.addEventListener(OPEN_PREFERENCES_EVENT, listener);
  return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, listener);
};
